import { useEffect } from 'react';
import { getServiceWorkerContainer, hasEventListenerApi } from '@/lib/serviceWorkerSupport';

/**
 * Silently activates a newly-installed service worker via SKIP_WAITING.
 *
 * 2026-05-15 (Andy): removed the user-facing "New version available"
 * toast that this hook used to surface. The toast was preemptive
 * noise — the chunk-mismatch failure modes it was supposed to prevent
 * are already handled silently by two other layers:
 *
 *   1. `lazyWithRetry()` — retries failed lazy imports once, then
 *      reloads. Catches the "old bundle tries to dynamic-import a
 *      hash that no longer exists" case.
 *   2. `ErrorBoundary` — detects ChunkLoadError / "Loading chunk"
 *      messages and force-reloads the page. Catches the "chunk
 *      fails to parse / MIME mismatch" case.
 *
 * Between those two layers, the user never needs to manually click
 * Refresh. The only thing this hook does now is tell the new SW to
 * activate immediately instead of waiting for all tabs to close
 * (SKIP_WAITING). That part is still useful because it accelerates
 * the recovery loop — chunks for the new deploy start loading
 * sooner — without ever showing UI.
 *
 * If we ever decide we DO want to nudge the user (e.g. for a security-
 * critical update), bring back a Sheet-style banner that doesn't
 * compete with toasts for attention. The previous Sonner toast was
 * jarring and shipped a "tech-bro app does updates wrong" vibe.
 */
export function useServiceWorkerUpdate() {
  useEffect(() => {
    const serviceWorker = getServiceWorkerContainer();
    if (!serviceWorker || !hasEventListenerApi(serviceWorker)) return;

    const cleanupFns: (() => void)[] = [];

    const checkForUpdates = async () => {
      try {
        const registration = await serviceWorker.getRegistration();
        if (!registration) return;

        await registration.update().catch(() => {});

        if (!hasEventListenerApi(registration)) return;

        // A new SW was found and is installing. Activate it silently so
        // the next navigation/fetch goes through it. No UI — chunk
        // failures (if any) are handled by lazyWithRetry + ErrorBoundary.
        const updateFoundHandler = () => {
          const newWorker = registration.installing;
          if (!newWorker || !hasEventListenerApi(newWorker)) return;

          const stateChangeHandler = () => {
            if (newWorker.state === 'installed' && serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          };
          newWorker.addEventListener('statechange', stateChangeHandler);
          cleanupFns.push(() => {
            newWorker.removeEventListener('statechange', stateChangeHandler);
          });
        };

        registration.addEventListener('updatefound', updateFoundHandler);
        cleanupFns.push(() => {
          registration.removeEventListener('updatefound', updateFoundHandler);
        });
      } catch {
        // Ignore transient network/deploy errors
      }
    };

    void checkForUpdates();

    return () => {
      cleanupFns.forEach(fn => fn());
    };
  }, []);
}
